import { appURLs } from "@/app/constants.mjs";
import LoLColors from "@/game-lol/constants/colors.mjs";
import { QUEUE_SYMBOLS } from "@/game-lol/constants/constants-queue-symbols.mjs";
import { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
import type { LolChampions } from "@/game-lol/models/lol-champions.mjs";
import type { LolItems } from "@/game-lol/models/lol-items.mjs";
import type { LolRunes } from "@/game-lol/models/lol-runes.mjs";
import type { LolSpells } from "@/game-lol/models/lol-spells.mjs";
import { patchVal } from "@/game-lol/utils/patch-utils.mjs";
import { ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";
import stringsToSymbols from "@/util/strings-to-symbols.mjs";
import SymbolMap from "@/util/symbol-map.mjs";
import symbolName from "@/util/symbol-name.mjs";

export { QUEUE_SYMBOLS } from "@/game-lol/constants/constants-queue-symbols.mjs";
export { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
export { mapRoleToSymbol, ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";

// Used internally in drafting.
export const INGAME_PHASES = [
  "DECLARING",
  "BANNING",
  "PICKING",
  "FINALIZING",
  "STARTING",
  "INPROGRESS",
  "UNKNOWN",
];

export const INGAME_TABS = [
  "suggestions",
  "builds",
  "probuilds",
  "builds-arena",
];

export const ARAM_TRADE_STATES = {
  available: "AVAILABLE",
  received: "RECEIVED",
};
// Drafting player tag count
export const MAX_TAGS = 3;
export const MAIN_ROLE_PERCENT = 0.75;
export const AUTOFILL_PERCENT = 0.2;

export const CHAMPION_PROFICIENCY = {
  otp: "OTP",
  lover: "LOVER",
  fan: "FAN",
};

export const MAX_GAME_TIME_IN_MINUTES = 200 * 60;

export const MAP_ICONS = {
  sr: `${appURLs.CDN_PLAIN}/blitz/lol/maps-icons/sr.png`,
  ha: `${appURLs.CDN_PLAIN}/blitz/lol/maps-icons/ha.png`,
  tft: `${appURLs.CDN_PLAIN}/blitz/lol/maps-icons/tft.png`,
  rgm: `${appURLs.CDN_PLAIN}/blitz/lol/maps-icons/rgm.png`,
  misc: `${appURLs.CDN_PLAIN}/blitz/lol/maps-icons/21.png`,
};

export const RANK_SYMBOL_TO_STR = new SymbolMap({
  [RANK_SYMBOLS.masterPlus]: {
    key: "master_plus",
    gql: "MASTER+",
    t: {
      name: "lol:filters.ranks.masterPlus",
      fallback: "Master+",
    },
    color: LoLColors.ranks.master.text,
  },
  [RANK_SYMBOLS.diamondPlus]: {
    key: "diamond_plus",
    gql: "DIAMOND+",
    t: {
      name: "lol:filters.ranks.diamondPlus",
      fallback: "Diamond+",
    },
    color: LoLColors.ranks.diamond.text,
  },
  [RANK_SYMBOLS.emeraldPlus]: {
    key: "emerald_plus",
    gql: "EMERALD+",
    t: {
      name: "lol:filters.ranks.emeraldPlus",
      fallback: "Emerald+",
    },
    color: LoLColors.ranks.emerald.text,
  },
  [RANK_SYMBOLS.platinumPlus]: {
    key: "platinum_plus",
    gql: "PLATINUM+",
    t: {
      name: "lol:filters.ranks.platPlus",
      fallback: "Platinum+",
    },
    color: LoLColors.ranks.platinum.text,
  },
  [RANK_SYMBOLS.challenger]: {
    key: "challenger",
    gql: "CHALLENGER",
    isApex: true,
    t: {
      name: "lol:filters.ranks.challenger",
      fallback: "Challenger",
    },

    color: LoLColors.ranks.challenger.text,
    nextRank: null,
  },
  [RANK_SYMBOLS.grandmaster]: {
    key: "grandmaster",
    gql: "GRANDMASTER",
    isApex: true,
    t: {
      name: "lol:filters.ranks.grandmaster",
      fallback: "Grandmaster",
    },
    color: LoLColors.ranks.grandmaster.text,
    nextRank: RANK_SYMBOLS.challenger,
  },
  [RANK_SYMBOLS.master]: {
    key: "master",
    gql: "MASTER",
    isApex: true,
    t: {
      name: "lol:filters.ranks.master",
      fallback: "Master",
    },
    color: LoLColors.ranks.master.text,
    nextRank: RANK_SYMBOLS.grandmaster,
  },
  [RANK_SYMBOLS.diamond]: {
    key: "diamond",
    gql: "DIAMOND",
    t: {
      name: "lol:filters.ranks.diamond",
      fallback: "Diamond",
    },
    color: LoLColors.ranks.diamond.text,
    nextRank: RANK_SYMBOLS.master,
  },
  [RANK_SYMBOLS.emerald]: {
    key: "emerald",
    gql: "EMERALD",
    t: {
      name: "lol:filters.ranks.emerald",
      fallback: "Emerald",
    },
    color: LoLColors.ranks.emerald.text,
    nextRank: RANK_SYMBOLS.diamond,
  },
  [RANK_SYMBOLS.platinum]: {
    key: "platinum",
    gql: "PLATINUM",
    t: {
      name: "lol:filters.ranks.platinum",
      fallback: "Platinum",
    },
    color: LoLColors.ranks.platinum.text,
    nextRank: RANK_SYMBOLS.emerald,
  },
  [RANK_SYMBOLS.gold]: {
    key: "gold",
    gql: "GOLD",
    t: {
      name: "lol:filters.ranks.gold",
      fallback: "Gold",
    },
    color: LoLColors.ranks.gold.text,
    nextRank: RANK_SYMBOLS.platinum,
  },
  [RANK_SYMBOLS.silver]: {
    key: "silver",
    gql: "SILVER",
    t: {
      name: "lol:filters.ranks.silver",
      fallback: "Silver",
    },
    color: LoLColors.ranks.silver.text,
    nextRank: RANK_SYMBOLS.gold,
  },
  [RANK_SYMBOLS.bronze]: {
    key: "bronze",
    gql: "BRONZE",
    t: {
      name: "lol:filters.ranks.bronze",
      fallback: "Bronze",
    },
    color: LoLColors.ranks.bronze.text,
    nextRank: RANK_SYMBOLS.silver,
  },
  [RANK_SYMBOLS.iron]: {
    key: "iron",
    gql: "IRON",
    t: {
      name: "lol:filters.ranks.iron",
      fallback: "Iron",
    },
    color: LoLColors.ranks.iron.text,
    nextRank: RANK_SYMBOLS.bronze,
  },
});

// TODO: key by symbols
// NOTE: the "datalake" key is used for the datalake API because their enums are different
export const REGION_LIST = [
  {
    key: "world",
    name: "World",
    gql: "WORLD",
    datalake: "WORLD",
    t: {
      name: "lol:regions.world.short",
      fallback: "World",
    },
  },
  {
    key: "na1",
    name: "NA",
    gql: "NA1",
    datalake: "NA",
    t: {
      name: "lol:regions.na.short",
      fallback: "NA",
    },
  },
  {
    key: "br1",
    name: "BR",
    gql: "BR1",
    datalake: "BR",
    t: {
      name: "lol:regions.br.short",
      fallback: "BR",
    },
  },
  {
    key: "eun1",
    name: "EUNE",
    gql: "EUN1",
    datalake: "EUNE",
    t: {
      name: "lol:regions.eune.short",
      fallback: "EUNE",
    },
  },
  {
    key: "euw1",
    name: "EUW",
    gql: "EUW1",
    datalake: "EUW",
    t: {
      name: "lol:regions.euw.short",
      fallback: "EUW",
    },
  },
  {
    key: "jp1",
    name: "JP",
    gql: "JP1",
    datalake: "JP",
    t: {
      name: "lol:regions.jp.short",
      fallback: "JP",
    },
  },
  {
    key: "kr",
    name: "KR",
    gql: "KR",
    datalake: "KR",
    t: {
      name: "lol:regions.kr.short",
      fallback: "KR",
    },
  },
  {
    key: "la1",
    name: "LAN",
    gql: "LA1",
    datalake: "LAN",
    t: {
      name: "lol:regions.lan.short",
      fallback: "LAN",
    },
  },
  {
    key: "la2",
    name: "LAS",
    gql: "LA2",
    datalake: "LAS",
    t: {
      name: "lol:regions.las.short",
      fallback: "LAS",
    },
  },
  {
    key: "me1",
    name: "MEA",
    gql: "ME1",
    datalake: "MEA",
    t: {
      name: "lol:regions.mea.short",
      fallback: "MEA",
    },
  },
  {
    key: "oc1",
    name: "OCE",
    gql: "OC1",
    datalake: "OCE",
    t: {
      name: "lol:regions.oce.short",
      fallback: "OCE",
    },
  },
  {
    key: "tr1",
    name: "TR",
    gql: "TR1",
    datalake: "TR",
    t: {
      name: "lol:regions.tr.short",
      fallback: "TR",
    },
  },
  {
    key: "ru",
    name: "RU",
    gql: "RU",
    datalake: "RU",
    t: {
      name: "lol:regions.ru.short",
      fallback: "RU",
    },
  },
  {
    key: "ph2",
    name: "PH",
    gql: "PH2",
    datalake: "PH",
    t: {
      name: "lol:regions.ph.short",
      fallback: "PH",
    },
  },
  {
    key: "sg2",
    name: "SG",
    gql: "SG2",
    datalake: "SG",
    t: {
      name: "lol:regions.sg.short",
      fallback: "SG",
    },
  },
  {
    key: "th2",
    name: "TH",
    gql: "TH2",
    datalake: "TH",
    t: {
      name: "lol:regions.th.short",
      fallback: "TH",
    },
  },
  {
    key: "tw2",
    name: "TW",
    gql: "TW2",
    datalake: "TW",
    t: {
      name: "lol:filters.regions.tw2",
      fallback: "TW",
    },
  },
  {
    key: "vn2",
    name: "VN",
    gql: "VN2",
    datalake: "VN",
    t: {
      name: "lol:regions.vn.short",
      fallback: "VN",
    },
  },
];

export const TEAMS = {
  BLUE: {
    id: 100,
    int: 1,
    riot: "ORDER",
    color: "var(--blue)",
  },
  RED: {
    id: 200,
    int: 2,
    riot: "CHAOS",
    color: "var(--red)",
  },
};

export const PREMADE_LABELS = {
  2: ["common:duo", "Duo"],
  3: ["common:trio", "Trio"],
  4: ["common:fourStack", "4-Stack"],
  5: ["common:fiveStack", "5-Stack"],
};

// Fallback for if GQL seasons request fails
// Rito is resetting ranks on splits now (mid-year)
// we dont know how that will effect season IDs
// but we are guessing they will just bump the id
// ex 2023/13 split 2 will be id 22
// https://www.leagueoflegends.com/en-us/news/game-updates/changes-to-ranked-in-2023/
export const SEASONS_FALLBACK = {
  9: "Season 7",
  11: "Season 8",
  12: "Preseason 9",
  13: "Season 9",
  14: "Preseason 10",
  15: "Season 10",
  16: "Preseason 11",
  17: "Season 11",
  18: "Preseason 12",
  19: "Season 12",
  20: "Preseason 13",
  21: "Season 13 - Split 1", // 2023 split 1
};

export const SEASONS_START = {
  20: new Date("January 10, 2022 06:00:00"), // 2022
  21: new Date("January 11, 2023 05:00:00"), // 2023 split 1
  22: new Date("July 19, 2023 06:00:00"), // SPECULATIVE ID & date for 2023 split 2... we dont know for sure
};

export const FILTER_SYMBOLS = stringsToSymbols([
  "searchText",
  "role",
  "duoRole",
  "tier",
  "mastery",
  "queue",
  "region",
  "patch",
  "matchup",
  "team",
  "victoryOnly",
]);

export const ROLE_SYMBOL_TO_STR = new SymbolMap({
  [ROLE_SYMBOLS.all]: {
    key: "all",
    gql: null,
    label: ["lol:roles.all", "All"],
    index: 0,
  },
  [ROLE_SYMBOLS.top]: {
    key: "top",
    gql: "TOP",
    label: ["lol:roles.top", "Top"],
    index: 1,
  },
  [ROLE_SYMBOLS.jungle]: {
    key: "jungle",
    gql: "JUNGLE",
    label: ["lol:roles.jungle", "Jungle"],
    index: 2,
  },
  [ROLE_SYMBOLS.mid]: {
    key: "mid",
    gql: "MID",
    label: ["lol:roles.mid", "Mid"],
    index: 3,
  },
  [ROLE_SYMBOLS.adc]: {
    key: "adc",
    gql: "BOT",
    label: ["lol:roles.adc", "ADC"],
    index: 4,
  },
  [ROLE_SYMBOLS.support]: {
    key: "support",
    gql: "SUPPORT",
    label: ["lol:roles.support", "Support"],
    index: 5,
  },
});

export const ROLE_INDEX = new SymbolMap({
  [ROLE_SYMBOLS.top]: 1,
  [ROLE_SYMBOLS.jungle]: 2,
  [ROLE_SYMBOLS.mid]: 3,
  [ROLE_SYMBOLS.adc]: 4,
  [ROLE_SYMBOLS.support]: 5,
  [ROLE_SYMBOLS.all]: 6,
});

export const ROLE_PAIRS = new SymbolMap({
  [ROLE_SYMBOLS.top]: ROLE_SYMBOLS.jungle,
  [ROLE_SYMBOLS.jungle]: ROLE_SYMBOLS.mid,
  [ROLE_SYMBOLS.mid]: ROLE_SYMBOLS.jungle,
  [ROLE_SYMBOLS.adc]: ROLE_SYMBOLS.support,
  [ROLE_SYMBOLS.support]: ROLE_SYMBOLS.adc,
  [ROLE_SYMBOLS.all]: ROLE_SYMBOLS.all,
});

export const ROLE_SYMBOL_TO_RIOT_STRING = new SymbolMap({
  [ROLE_SYMBOLS.adc]: "BOTTOM",
  [ROLE_SYMBOLS.jungle]: "JUNGLE",
  [ROLE_SYMBOLS.support]: "UTILITY",
  [ROLE_SYMBOLS.mid]: "MIDDLE",
  [ROLE_SYMBOLS.top]: "TOP",
});

export const ROLE_MAP: Record<string, symbol> = {
  null: ROLE_SYMBOLS.all,
  all: ROLE_SYMBOLS.all,

  top: ROLE_SYMBOLS.top,

  jungle: ROLE_SYMBOLS.jungle,
  jng: ROLE_SYMBOLS.jungle,

  middle: ROLE_SYMBOLS.mid,
  mid: ROLE_SYMBOLS.mid,
  solo: ROLE_SYMBOLS.mid,

  bottom: ROLE_SYMBOLS.adc,
  bot: ROLE_SYMBOLS.adc,
  adc: ROLE_SYMBOLS.adc,
  carry: ROLE_SYMBOLS.adc,

  sup: ROLE_SYMBOLS.support,
  support: ROLE_SYMBOLS.support,
  utility: ROLE_SYMBOLS.support,
  duo: ROLE_SYMBOLS.support,
};

export const REGION_SYMBOL_NA: unique symbol = symbolName("na");
export const REGION_SYMBOL_BR: unique symbol = symbolName("br");
export const REGION_SYMBOL_EUNE: unique symbol = symbolName("eune");
export const REGION_SYMBOL_EUW: unique symbol = symbolName("euw");
export const REGION_SYMBOL_LAN: unique symbol = symbolName("lan");
export const REGION_SYMBOL_LAS: unique symbol = symbolName("las");
export const REGION_SYMBOL_MEA: unique symbol = symbolName("mea");
export const REGION_SYMBOL_OCE: unique symbol = symbolName("oce");
export const REGION_SYMBOL_RU: unique symbol = symbolName("ru");
export const REGION_SYMBOL_TR: unique symbol = symbolName("tr");
export const REGION_SYMBOL_JP: unique symbol = symbolName("jp");
export const REGION_SYMBOL_KR: unique symbol = symbolName("kr");
export const REGION_SYMBOL_PBE: unique symbol = symbolName("pbe");
export const REGION_SYMBOL_PH: unique symbol = symbolName("ph");
export const REGION_SYMBOL_SG: unique symbol = symbolName("sg");
export const REGION_SYMBOL_TH: unique symbol = symbolName("th");
export const REGION_SYMBOL_TW: unique symbol = symbolName("tw");
export const REGION_SYMBOL_VN: unique symbol = symbolName("vn");

export const REGION_SYMBOLS = [
  REGION_SYMBOL_NA,
  REGION_SYMBOL_BR,
  REGION_SYMBOL_EUNE,
  REGION_SYMBOL_EUW,
  REGION_SYMBOL_LAN,
  REGION_SYMBOL_LAS,
  REGION_SYMBOL_MEA,
  REGION_SYMBOL_OCE,
  REGION_SYMBOL_RU,
  REGION_SYMBOL_TR,
  REGION_SYMBOL_JP,
  REGION_SYMBOL_KR,
  REGION_SYMBOL_PBE,
  REGION_SYMBOL_PH,
  REGION_SYMBOL_SG,
  REGION_SYMBOL_TH,
  REGION_SYMBOL_TW,
  REGION_SYMBOL_VN,
] as const;

export type LolRegionSymbol = (typeof REGION_SYMBOLS)[number];

export const PRO_LEAGUES = {
  worlds: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/worlds.webp`,
  msi: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/msi.webp`,
  lcs: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lcs.webp`,
  lck: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lck.webp`,
  lec: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lec.webp`,
  lpl: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lpl.webp`,
  tcl: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/tcl.webp`,
  cblol: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/cblol.webp`,
  lla: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lla.webp`,
  lco: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lco.webp`,
  ljl: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/ljl.webp`,
  vcs: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/vcs.webp`,
  lplol: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lplol.webp`,
  em: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/em.webp`,
  prime: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/prime.webp`,
  lcl: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/lcl.webp`,
  pcs: `${appURLs.CDN_PLAIN}/blitz/lol/competitive_regions/pcs.webp`,
};

export const REGION_MAP = {
  // RIOT Server Regions
  na: REGION_SYMBOL_NA,
  na1: REGION_SYMBOL_NA,
  br: REGION_SYMBOL_BR,
  br1: REGION_SYMBOL_BR,
  eune: REGION_SYMBOL_EUNE,
  eun1: REGION_SYMBOL_EUNE,
  euw: REGION_SYMBOL_EUW,
  euw1: REGION_SYMBOL_EUW,
  lan: REGION_SYMBOL_LAN,
  la1: REGION_SYMBOL_LAN,
  las: REGION_SYMBOL_LAS,
  la2: REGION_SYMBOL_LAS,
  mea: REGION_SYMBOL_MEA,
  me1: REGION_SYMBOL_MEA,
  oce: REGION_SYMBOL_OCE,
  oc1: REGION_SYMBOL_OCE,
  ru: REGION_SYMBOL_RU,
  ru1: REGION_SYMBOL_RU,
  tr: REGION_SYMBOL_TR,
  tr1: REGION_SYMBOL_TR,
  jp: REGION_SYMBOL_JP,
  jp1: REGION_SYMBOL_JP,
  kr: REGION_SYMBOL_KR,
  pbe: REGION_SYMBOL_PBE,
  ph: REGION_SYMBOL_PH,
  ph2: REGION_SYMBOL_PH,
  sg: REGION_SYMBOL_SG,
  sg2: REGION_SYMBOL_SG,
  tw: REGION_SYMBOL_TW,
  tw2: REGION_SYMBOL_TW,
  vn: REGION_SYMBOL_VN,
  vn2: REGION_SYMBOL_VN,
  th: REGION_SYMBOL_TH,
  th2: REGION_SYMBOL_TH,
  // TODO: (Need to do something with Tencent Server regions)
} as const;

// Queue ::WITH:: "roles"
export const SCORABLE_QUEUE_TYPES = [
  QUEUE_SYMBOLS.normalDraft,
  QUEUE_SYMBOLS.rankedSoloDuo,
  QUEUE_SYMBOLS.rankedFlex,
  QUEUE_SYMBOLS.normalBlind,
  QUEUE_SYMBOLS.custom,
  QUEUE_SYMBOLS.clash,
  QUEUE_SYMBOLS.ultimateSpellbook,
  QUEUE_SYMBOLS.swiftplay,
];

// Queue ::WITHOUT:: "roles"
export const NONSCORABLE_QUEUE_TYPES = [
  QUEUE_SYMBOLS.aram,
  QUEUE_SYMBOLS.urf,
  QUEUE_SYMBOLS.arena,
  QUEUE_SYMBOLS.brawl,
  QUEUE_SYMBOLS.nexusBlitz,
  QUEUE_SYMBOLS.oneForAll,
  QUEUE_SYMBOLS.doomBots,
  QUEUE_SYMBOLS.trialsOfDoom,
];

// Minimum role percent for meta stats
export const QUEUE_ROLE_PERCENT = {
  [QUEUE_SYMBOLS.rankedSoloDuo]: 0.1,
  [QUEUE_SYMBOLS.rankedFlex]: 0.1,
};

export const QUEUE_TYPES = {
  "-1": "CUSTOM",
  0: "CUSTOM",
  2: "NORMAL",
  4: "RANKED_SOLO",
  6: "RANKED_PREMADE",
  7: "CO_OP_VS_AI",
  8: "TWISTED_TREELINE",
  9: "RANKED_TWISTED_TREELINE",
  14: "DRAFT_PICK",
  16: "BLIND_PICK_5V5_DOMINION",
  17: "DRAFT_PICK_5V5_DOMINION",
  25: "DOMINION_CO_OP_VS_AI",
  31: "CO_OP_VS_AI_INTRO_BOT",
  32: "CO_OP_VS_AI_BEGINNER_BOT",
  33: "CO_OP_VS_AI_INTERMEDIATE_BOT",
  41: "RANKED_TEAM_3V3",
  42: "RANKED_TEAM_5V5",
  52: "CO_OP_VS_AI",
  61: "TEAM_BUILDER",
  65: "ARAM",
  72: "SNOWDOWN_SHOWDOWN_1V1",
  73: "SNOWDOWN_SHOWDOWN_2V2",
  75: "HEXAKILL_6V6",
  76: "ULTRA_RAPID_FIRE",
  78: "MIRRORED_ONE_FOR_ALL",
  83: "CO_OP_VS_AI_ULTRA_RAPID_FIRE",
  91: "DOOM_BOTS_RANK_1",
  92: "DOOM_BOTS_RANK_2",
  93: "DOOM_BOTS_RANK_5",
  96: "ASCENSION",
  98: "TWISTED_TREELINE_6V6_HEXAKILL",
  100: "BUTCHERS_BRIDGE_5V5_ARAM",
  300: "KING_PORO",
  310: "NEMESIS",
  313: "BLACK_MARKET_BRAWLERS",
  315: "NEXUS_SIEGE",
  317: "DEFINITELY_NOT_DOMINION",
  318: "ALL_RANDOM_URF",
  325: "ALL_RANDOM",
  400: "DRAFT_PICK",
  410: "RANKED_DYNAMIC",
  420: "RANKED_SOLO",
  430: "NORMAL",
  440: "RANKED_FLEX",
  450: "ARAM",
  460: "TWISTED_TREELINE",
  470: "RANKED_TWISTED_TREELINE",
  600: "BLOOD_HUNT_ASSASSIN",
  610: "DARK_STAR",
  700: "CLASH",
  720: "CLASH_ARAM",
  721: "CLASH_ARAM",
  740: "CLASH_URF",
  741: "CLASH_URF",
  800: "CO_OP_VS_AI_INTERMEDIATE_BOT",
  810: "CO_OP_VS_AI_INTRO_BOT",
  820: "CO_OP_VS_AI_BEGINNER_BOT",
  830: "CO_OP_VS_AI_INTRO_BOT",
  840: "CO_OP_VS_AI_BEGINNER_BOT",
  850: "CO_OP_VS_AI_INTERMEDIATE_BOT",
  900: "URF",
  920: "KING_PORO",
  940: "NEXUS_SIEGE",
  980: "GUARDIAN_INVASION",
  990: "GUARDIAN_INVASION_ONSLAUGHT",
  1000: "OVERCHARGED",
  1010: "URF",
  1020: "ONE_FOR_ALL",
  1030: "ODYSSEY_INTRO",
  1040: "ODYSSEY_CADET",
  1050: "ODYSSEY_CREWMEMBER",
  1060: "ODYSSEY_CAPTAIN",
  1070: "ODYSSEY_ONSLAUGHT",
  1200: "NEXUS_BLITZ",
  1300: "NEXUS_BLITZ",
  1400: "ULTIMATE_SPELLBOOK",
  1090: "TEAMFIGHT_TACTICS",
  1091: "TEAMFIGHT_TACTICS_ONE_VS_ZERO",
  1092: "TEAMFIGHT_TACTICS_TWO_VS_ZERO",
  1100: "RANKED_TEAMFIGHT_TACTICS",
  1110: "TEAMFIGHT_TACTICS_TUTORIAL",
  1111: "TEAMFIGHT_TACTICS_SIMULATION",
  1130: "TEAMFIGHT_TACTICS_HYPER_ROLL",
  1150: "TEAMFIGHT_TACTICS_DOUBLE_UP",
  1170: "TEAMFIGHT_TACTICS_FORTUNES_FAVOR",
  1190: "CHONCC_TREASURE_TFT",
  1210: "CHONCC_TREASURE_TFT",
  1220: "TOCKERS_TRIALS_TFT",
  1700: "CHERRY",
  1710: "CHERRY",
  1900: "URF",
  6000: "TEAMFIGHT_TACTICS_GALAXIES_REVIVIAL",
  // 6100: "TEAMFIGHT_TACTICS_FESTIVAL_OF_BEASTS_REVIVIAL",
  6100: "TEAMFIGHT_TACTICS_UNCHARTED_REALMS_REVIVIAL",
  6110: "TEAMFIGHT_TACTICS_REMIX_RUMBLE_REVIVIAL",
  6120: "PENGUS_PARTY_TFT",
  6130: "AO_SHINS_ASCENT",
};

const IRON = "IRON";
const BRONZE = "BRONZE";
const SILVER = "SILVER";
const GOLD = "GOLD";
const PLATINUM = "PLATINUM";
const DIAMOND = "DIAMOND";
const MASTER = "MASTER";
const GRANDMASTER = "GRANDMASTER";
const CHALLENGER = "CHALLENGER";

export const RANK_TIERS = [
  IRON,
  BRONZE,
  SILVER,
  GOLD,
  PLATINUM,
  DIAMOND,
  MASTER,
  GRANDMASTER,
  CHALLENGER,
];

export const RANK_TIERS_SYMBOLS = [
  RANK_SYMBOLS.iron,
  RANK_SYMBOLS.bronze,
  RANK_SYMBOLS.silver,
  RANK_SYMBOLS.gold,
  RANK_SYMBOLS.platinum,
  RANK_SYMBOLS.diamond,
  RANK_SYMBOLS.master,
  RANK_SYMBOLS.grandmaster,
  RANK_SYMBOLS.challenger,
];

export const TIER_NONE = "NONE";

export const TIER_UNRANKED = [
  RANK_SYMBOLS.master,
  RANK_SYMBOLS.grandmaster,
  RANK_SYMBOLS.challenger,
  MASTER,
  GRANDMASTER,
  CHALLENGER,
];

export const GAME_LOL_RANK_COLORS = {
  iron: {
    fill: "#817678",
    text: "#a29294",
  },
  bronze: {
    fill: "#9F6347",
    text: "#b97452",
  },
  silver: {
    fill: "#80989D",
    text: "#a2c1c7",
  },
  gold: {
    fill: "#CD8837",
    text: "#f1a64e",
  },
  platinum: {
    fill: "#1c91b6",
    text: "#14c0f5",
  },
  emerald: {
    fill: "#519847",
    text: "#56d744",
  },
  diamond: {
    fill: "#8141EB",
    text: "#9556ff",
  },
  master: {
    fill: "#9D48E0",
    text: "#A952E5",
  },
  grandmaster: {
    fill: "#D94444",
    text: "#ef4f4f",
  },
  challenger: {
    fill: "#F4C874",
    text: "#F4C874",
  },
  none: {
    fill: "var(--shade6)",
    text: "var(--shade3)",
  },
  wood: {
    fill: "#80554D",
    text: "#ac756a",
  },
  gladiator: {
    fill: "#D967E4",
    text: "#D967E4",
  },
};

export const SEASON_FILTERS = {
  PRESEASON: "preSeason",
  SEASON: "season",
  PREV20: "prev20",
};

export const IS_APEX = {
  MASTER: true,
  GRANDMASTER: true,
  CHALLENGER: true,
};
export const RANK_VALS = {
  IRON_IV: 0,
  IRON_III: 100,
  IRON_II: 200,
  IRON_I: 300,

  BRONZE_IV: 400,
  BRONZE_III: 500,
  BRONZE_II: 600,
  BRONZE_I: 700,

  SILVER_IV: 800,
  SILVER_III: 900,
  SILVER_II: 1000,
  SILVER_I: 1100,

  GOLD_IV: 1200,
  GOLD_III: 1300,
  GOLD_II: 1400,
  GOLD_I: 1500,

  PLATINUM_IV: 1600,
  PLATINUM_III: 1700,
  PLATINUM_II: 1800,
  PLATINUM_I: 1900,

  EMERALD_IV: 2000,
  EMERALD_III: 2100,
  EMERALD_II: 2200,
  EMERALD_I: 2300,

  DIAMOND_IV: 2400,
  DIAMOND_III: 2500,
  DIAMOND_II: 2600,
  DIAMOND_I: 2700,

  APEX: 2800,
};

export const ROLE_SYMBOL_TO_TAB_STRING = new SymbolMap({
  [ROLE_SYMBOLS.adc]: "bot",
  [ROLE_SYMBOLS.jungle]: "jungle",
  [ROLE_SYMBOLS.support]: "support",
  [ROLE_SYMBOLS.mid]: "mid",
  [ROLE_SYMBOLS.top]: "top",
});

export const SKILL_COLORS = ["red", "green", "blue", "yellow"];
export const SKILL_HOTKEYS = ["Q", "W", "E", "R"];
export const NOT_PURCHASABLE_ITEMS = [
  1500, 1501, 1502, 1503, 1504, 1505, 1506, 1507, 1508, 1509, 1510, 1511, 1512,
  1515, 2001, 2007, 2008, 3901, 3902, 3903, 7000, 7001, 7002, 7003, 7004, 7005,
  7006, 7007, 7008, 7009, 7010, 7011, 7012, 7013, 7014, 7015, 7016, 7017, 7018,
  7019, 7020, 7021, 7022, 7050,
];

export const BUILD_TYPES = {
  general: "GENERAL",
  matchup: "MATCHUP",
  probuild: "PROBUILD",
  PREMIUM: "PREMIUM",
  PREMIUM_MATCHUP: "PREMIUM_MATCHUP",
  COMPETITIVE_MATCH: "COMPETITIVE_MATCH",
};

export const APHELIOS_ID = 523;

export const CHAMPION_LEVELS = new Array(18).fill(0).map((_, i) => i + 1);

/**
 * Rune IDs mapped to their row
 * Specifcally useful for secondary runes
 */
export const RUNE_ROWS = {
  8126: 1,
  8139: 1,
  8143: 1,
  8136: 2,
  8120: 2,
  8138: 2,
  8135: 3,
  8134: 3,
  8105: 3,
  8106: 3,
  8306: 1,
  8304: 1,
  8313: 1,
  8321: 2,
  8316: 3,
  8345: 2,
  8347: 3,
  8410: 3,
  8352: 3,
  9101: 1,
  9111: 1,
  8009: 1,
  9104: 2,
  9105: 2,
  9103: 2,
  8014: 3,
  8017: 3,
  8299: 3,
  8446: 1,
  8463: 1,
  8401: 1,
  8429: 2,
  8444: 2,
  8473: 2,
  8451: 3,
  8453: 3,
  8242: 3,
  8224: 1,
  8226: 1,
  8275: 1,
  8210: 2,
  8234: 2,
  8233: 2,
  8237: 3,
  8232: 3,
  8236: 3,
};

export const SHARDS = {
  MR: 5003,
  ARMOR: 5002,
  HEALTHSCALING: 5001,
  HEALTH: 5011,
  ADAPTIVE: 5008,
  ATKSPD: 5005,
  CDR: 5007,
  TENACITYSLOW: 5013,
  MOVESPEED: 5010,
};

const shardMapOLD = [
  [SHARDS.ADAPTIVE, SHARDS.ATKSPD, SHARDS.CDR],
  [SHARDS.ADAPTIVE, SHARDS.ARMOR, SHARDS.MR],
  [SHARDS.HEALTHSCALING, SHARDS.ARMOR, SHARDS.MR],
];
const shardMapNEW = [
  [SHARDS.ADAPTIVE, SHARDS.ATKSPD, SHARDS.CDR],
  [SHARDS.ADAPTIVE, SHARDS.MOVESPEED, SHARDS.HEALTHSCALING],
  [SHARDS.HEALTH, SHARDS.TENACITYSLOW, SHARDS.HEALTHSCALING],
];

export function shardMap(patch = "") {
  if (patchVal(patch) < 1402) {
    return shardMapOLD;
  }

  return shardMapNEW;
}

export const shardInfo = {
  5001: {
    id: 5001,
    name: ["lol:shardInfo.scaling_health", "Scaling Health"],
    desc: [
      "lol:shardInfo.scaling_health_description_2024",
      "+10-180 HP (based on level)",
    ],
    stat: "HEALTH",
  },
  5002: {
    id: 5002,
    name: ["lol:shardInfo.armor", "Armor"],
    desc: ["lol:shardInfo.armor_description", "+6 Armor"],
    stat: "ARMOR",
  },
  5003: {
    id: 5003,
    name: ["lol:shardInfo.magic_resist", "Magic Resist"],
    desc: ["lol:shardInfo.magic_resist_description", "+8 MR"],
    stat: "MR",
  },
  5005: {
    id: 5005,
    name: ["lol:shardInfo.attack_speed", "Attack Speed"],
    desc: ["lol:shardInfo.attack_speed_description", "+10% Attack Speed"],
    stat: "AS",
  },
  5007: {
    id: 5007,
    name: ["lol:shardInfo.ability_haste", "Ability Haste"],
    desc: ["lol:shardInfo.ability_haste_description", "+8 Ability Haste"],
    stat: "CDR",
  },
  5008: {
    id: 5008,
    name: ["lol:shardInfo.adaptive_force", "Adaptive Force"],
    desc: ["lol:shardInfo.adaptive_force_description", "+9 Adaptive Force"],
    stat: "ADAPTIVE",
  },
  5010: {
    id: 5010,
    name: ["lol:shardInfo.move_speed", "Move Speed"],
    desc: ["lol:shardInfo.move_speed_description", "+2% Movement Speed"],
    stat: "MOVESPEED",
  },
  5011: {
    id: 5011,
    name: ["lol:shardInfo.health", "Health"],
    desc: ["lol:shardInfo.health_description", "+65 Health"],
    stat: "HEALTH",
  },
  5013: {
    id: 5013,
    name: ["lol:shardInfo.tenacity_slow_resist", "Tenacity & Slow Resist"],
    desc: [
      "lol:shardInfo.tenacity_slow_resist_description",
      "+10% Tenacity and Slow Resist",
    ],
    stat: "TENACITY",
  },
};

export const shardIdList = [5008, 5005, 5007, 5002, 5003, 5001];
export const REGIONS_TO_SERVICES = {
  NA: "na1",
  EUNE: "eun1",
  EUW: "euw1",
  JP: "jp1",
  KR: "kr",
  LAN: "la1",
  LAS: "la2",
  MEA: "me1",
  BR: "br1",
  OCE: "oc1",
  TR: "tr1",
  RU: "ru",
  SG: "sg2",
  VN: "vn2",
  TH: "th2",
  TW: "tw2",
  PH: "ph2",
} as const;

export const SERVICES_TO_REGIONS = (
  Object.keys(REGIONS_TO_SERVICES) as Array<keyof typeof REGIONS_TO_SERVICES>
).reduce(
  (obj, key) => ({
    ...obj,
    [REGIONS_TO_SERVICES[key]]: key,
  }),
  {},
);

export const STAT_TILE_TYPES = {
  cs: "CS",
  visionScore: "VisionScore",
  damage: "Damage",
  kda: "KDA",
  supportItem: "SupportItem",
  headshot: "Headshot",
  valKda: "valKda",
  valFirstblood: "valFirstblood",
};

export const STAT_TILE_COLORS = {
  gold: {
    key: "gold",
    bgColor: "var(--subscriber)",
    color: "var(--subscriber)",
    border: "var(--subscriber)",
  },
  green: {
    key: "green",
    bgColor: "var(--bg-green)",
    color: "var(--perf-pos3)",
    border: "var(--perf-pos3)",
  },
  red: {
    key: "red",
    bgColor: "var(--bg-red)",
    color: "var(--perf-neg3)",
    border: "var(--perf-neg3)",
  },
  // This is a hashmap of ONLY new champion ability images
};

export const PROS_FALLBACK_IMAGE = `${appURLs.SOLOMID_RESOURCE_URL}/probuilds/img/pros/170x240/misc.png`;

// This is a hashmap of ONLY new champion ability images
export const CHAMPION_ABILITY_MAP = {
  // Fiddlesticks_P:
  //   "//raw.communitydragon.org/pbe/game/assets/characters/fiddlesticks/hud/icons2d/fiddlesticksp.png",
};

export const DEFAULT_COUNT_PER_LOAD = 20;

export const SUPPORT_ITEM = {
  one: 3865, // tier 1
  two: 3866, // tier 2
  three: 3867, // tier 3
};

export const BUILD_CATEGORIES = {
  playstyle: "playstyle",
  matchup: "matchup",
  probuild: "probuild",
  competitive: "competitive",
  arena: "arena",
};

export const RUNES_SPECIAL = {
  magicalFootwear: 8304,
  timeWarpTonic: 8352,
  hexflash: 8306,
  jackOfAllTrades: 8316,
};

export const REQUIRED_RUNES = 9;
export const REQUIRED_SUMMONER_SPELLS = 2;

export const SUMMONER_SPELL_IDS = {
  flash: 4,
  ghost: 6,
  smite: 11,
  snowball: 32,
  ignite: 14,
  teleport: 12,
  exhaust: 3,
};

export const LANE_PHASE_END = 14;

export const KEYSTONE_STYLES = {
  8005: ["lol:build.playstyles.keystone.damageAmp", "Damage Amp"],
  8008: ["lol:build.playstyles.keystone.attackSpeed", "Attack Speed"],
  8021: ["lol:build.playstyles.keystone.mobility", "Mobility"],
  8010: ["lol:build.playstyles.keystone.fighter", "Fighter"],
  8112: ["lol:build.playstyles.keystone.burst", "Burst"],
  8124: ["lol:build.playstyles.keystone.engage", "Engage"],
  8128: ["lol:build.playstyles.keystone.execute", "Execute"],
  9923: ["lol:build.playstyles.keystone.attackSpeed", "Attack Speed"],
  8214: ["lol:build.playstyles.keystone.harass", "Harass"],
  8229: ["lol:build.playstyles.keystone.poke", "Poke"],
  8230: ["lol:build.playstyles.keystone.mobility", "Mobility"],
  8437: ["lol:build.playstyles.keystone.sustain", "Sustain"],
  8439: ["lol:build.playstyles.keystone.tank", "Tank"],
  8465: ["lol:build.playstyles.keystone.shielding", "Shielding"],
  8351: ["lol:build.playstyles.keystone.onHitSlow", "On-Hit Slow"],
  8360: ["lol:build.playstyles.keystone.scaling", "Scaling"],
  8358: ["lol:build.playstyles.keystone.adaptive", "Adaptive"],
  8369: ["lol:build.playstyles.keystone.adaptive", "Adaptive"],
};

export const CANVAS_HEIGHT = 380;
export const CANVAS_WIDTH = 672;

export const TILES_DELAY = 0.5; // [s]

// Phases used in gameflow session
export const PHASE_UNKNOWN = "Unknown";
export const PHASE_NONE = "None";
export const PHASE_GAME_START = "GameStart";
export const PHASE_WAITING_FOR_STATS = "WaitingForStats";
export const PHASE_IN_PROGRESS = "InProgress";
export const PHASE_LOBBY = "Lobby";
export const PHASE_PRE_END_OF_GAME = "PreEndOfGame";
export const PHASE_END_OF_GAME = "EndOfGame";
export const PHASE_IN_CHAMP_SELECT = "ChampSelect";
export const PHASE_MATCHMAKING = "Matchmaking";
export const PHASE_RECONNECT = "Reconnect";
export const PHASE_READY_CHECK = "ReadyCheck";

export const IN_GAME_PHASES = new Set([
  PHASE_GAME_START,
  PHASE_IN_PROGRESS,
  PHASE_PRE_END_OF_GAME,
  PHASE_END_OF_GAME,
  PHASE_WAITING_FOR_STATS,
  PHASE_RECONNECT,
]);

// These constants are only used in gameflow.
export const MAP_ID = makeStrictKeysObject({
  NEXUS_BLITZ: 21,
  TFT: 22,
  SUMMONERS_RIFT: 11,
  HOWLING_ABYSS: 12,
  RINGS_OF_WRATH: 30,
  SWARM: 33,
});

export const BAR_ANIMATION_DURATION = 0.2; // duration for animation [s]
export const BAR_SHOW_INTERVAL = 0.05; // Interval to show icons, [s]

export const scoreWeights = {
  distribution: {
    you: {
      ratio: 0.8,
      offset: 12,
    },
  },
};

export const FLASH_PLACEMENT_LEFT = 0;
export const FLASH_PLACEMENT_RIGHT = 1;
export const FLASH_PLACEMENT_NULL = -1;
export const flashPlacementOptions = [
  FLASH_PLACEMENT_LEFT,
  FLASH_PLACEMENT_RIGHT,
];

export const ROUTES = {
  inGame: "/lol/in-game",
};

export const LOBBY_ID = 0;

// These are internal LoL client events used for hooking into.
export const EVENT_LOL_ENTER_GAME = "EVENT_LOL_ENTER_GAME";
export const EVENT_LOL_EXIT_GAME = "EVENT_LOL_EXIT_GAME";
export const EVENT_LOL_GET_OVERLAY_DATA = "EVENT_LOL_GET_OVERLAY_DATA";
export const EVENT_LOL_BUILD_IMPORT_FAILED = "EVENT_LOL_BUILD_IMPORT_FAILED";
export const EVENT_LOL_IMPORT_ERROR_RUNES = "EVENT_LOL_IMPORT_ERROR_RUNES";
export const EVENT_LOL_IMPORT_SUCCESS_RUNES = "EVENT_LOL_IMPORT_SUCCESS_RUNES";
export const EVENT_LOL_IMPORT_ERROR_SPELLS = "EVENT_LOL_IMPORT_ERROR_SPELLS";
export const EVENT_LOL_IMPORT_SUCCESS_SPELLS =
  "EVENT_LOL_IMPORT_SUCCESS_SPELLS";
export const EVENT_LOL_ARENA_POSTMATCH = "EVENT_LOL_ARENA_POSTMATCH";
export const EVENT_LOL_BRAWL_POSTMATCH = "EVENT_LOL_BRAWL_POSTMATCH";
export const EVENT_LOL_ARAM_MAYHEM_POSTMATCH =
  "EVENT_LOL_ARAM_MAYHEM_POSTMATCH";
export const EVENT_LOL_TIMELINE_LOADED = "EVENT_LOL_TIMELINE_LOADED";

export const EVENT_LOL_FRIENDS = "EVENT_LOL_FRIENDS";

export const EVENT_TFT_ENTER_GAME = "EVENT_TFT_ENTER_GAME";
export const EVENT_TFT_EXIT_GAME = "EVENT_TFT_EXIT_GAME";
export const EVENT_TFT_LP_CHANGE = "EVENT_TFT_LP_CHANGE";
export const EVENT_TFT_POSTMATCH = "EVENT_TFT_POSTMATCH";
export const EVENT_TFT_ROUND_DATA = "EVENT_TFT_ROUND_DATA";
export const EVENT_TFT_ROUND_STATE = "EVENT_TFT_ROUND_STATE";
export const EVENT_TFT_OVERLAY_USAGE = "EVENT_TFT_OVERLAY_USAGE";

export interface LolRuneDetails {
  id: number;
  tree: number;
  name: string;
  desc: string;
}

export interface LolStaticData {
  [languageCode: string]: {
    [patch: string]: {
      spells?: LolSpells;
      runes?: LolRunes;
      champions?: LolChampions;
      items?: LolItems;

      runeDetails?: {
        [runeId: number]: LolRuneDetails;
      };
    };
  };
}
